import React from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, ExternalLink, Lock } from "lucide-react";
import {
    Callout,
    Pill,
    VbEmpty,
    VbError,
    VbLoading,
    Select,
    SelectItem,
    Table,
    TableHead,
    TableRow,
    VbButton,
} from "@/components/vb";
import PlanCard, { type BillingCadence } from "@/components/billing/PlanCard";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useWorkspaceRole } from "@/hooks/useWorkspaceRole";
import { listCreditTransactions } from "@/services/workspaceService";
import { getOnboardingStatus } from "@/services/onboardingService";
import { CreditTransaction } from "@/types/billing";
import {
    buildCreditPurchaseReference,
    redirectToStripeWithReturnPath,
} from "@/lib/stripeNavigation";

const STRIPE_PLAN_URL = import.meta.env.VITE_STRIPE_PLAN;
const STRIPE_ONE_TIME_CREDIT_PURCHASE_URL = import.meta.env.VITE_STRIPE_ONE_TIME_CREDIT_PURCHASE;
// The V2 payment links — the same four onboarding uses. The legacy
// STANDARD_*/PREMIUM_* links are gone with the tiers they sold.
const STRIPE_V2_MONTHLY_10 = import.meta.env.VITE_STRIPE_V2_MONTHLY_10;
const STRIPE_V2_MONTHLY_30 = import.meta.env.VITE_STRIPE_V2_MONTHLY_30;
const STRIPE_V2_MONTHLY = import.meta.env.VITE_STRIPE_V2_MONTHLY;
const STRIPE_V2_ANNUALLY = import.meta.env.VITE_STRIPE_V2_ANNUALLY;

const LEDGER_PAGE_SIZE = 10;
const LEDGER_COLUMNS = "minmax(0,1.4fr) minmax(0,1.4fr) minmax(0,0.6fr) minmax(0,1fr) minmax(0,1.4fr)";

/** Backend plan codes → what to call them on screen. `BillingAccount.plan`
 *  delegates to the account owner's `CustomUser.plan`, so this is the workspace's
 *  real tier — not the viewer's. */
const PLAN_LABELS: Record<string, string> = {
    free: "Free",
    v2_monthly: "AI Trust Signals · Monthly",
    v2_annual: "AI Trust Signals · Annual",
    standard_monthly: "Standard · Monthly",
    standard_yearly: "Standard · Yearly",
    premium_monthly: "Premium · Monthly",
    premium_yearly: "Premium · Yearly",
    personalized: "Personalized",
};

const formatTransactionDate = (date: string): string => {
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime())
        ? "Unknown"
        : parsed.toLocaleString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
          });
};

const formatDay = (date?: string | null): string | null => {
    if (!date) return null;
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime())
        ? null
        : parsed.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const formatReason = (reason: string): string =>
    reason
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");

const SettingsBilling: React.FC = () => {
    const { user } = useAuth();
    const { isBillingAccountOwner: isBillingOwner } = useWorkspaceRole();
    const location = useLocation();
    const { activeWorkspace, billingAccount, workspaces, loading: workspaceLoading } = useWorkspace();

    const [cadence, setCadence] = React.useState<BillingCadence>("monthly");
    const [offerCode, setOfferCode] = React.useState<string | null>(null);
    const [selectedLedgerWorkspaceId, setSelectedLedgerWorkspaceId] = React.useState<string>("all");
    const [transactions, setTransactions] = React.useState<CreditTransaction[]>([]);
    const [transactionsCount, setTransactionsCount] = React.useState(0);
    const [transactionsLoading, setTransactionsLoading] = React.useState(false);
    const [transactionsError, setTransactionsError] = React.useState<string | null>(null);
    const [ledgerPage, setLedgerPage] = React.useState(1);
    // Bumped by the ledger's Retry so the load effect actually re-runs.
    const [ledgerReloadKey, setLedgerReloadKey] = React.useState(0);

    // ── Who is looking, and at whose billing ────────────────────────────────
    // Everything here reads the ACTIVE WORKSPACE's billing account. The old
    // panel mixed scopes — workspace-scoped credits/seats above a fork on the
    // viewer's own `user.plan` — so a member of someone else's paid workspace
    // saw that workspace's credits above an upsell for their own free account.
    const plan = billingAccount?.plan ?? "free";
    const isPaid = plan !== "free";
    const isTrialing = billingAccount?.subscription_status === "trialing";
    const trialEnds = formatDay(billingAccount?.trial_end);
    const renewsOn = formatDay(billingAccount?.current_period_end);
    const totalCredits =
        billingAccount?.total_credits ??
        (billingAccount?.credits ?? 0) + (billingAccount?.purchased_credits ?? 0);

    const selectedLedgerWorkspace = React.useMemo(() => {
        if (selectedLedgerWorkspaceId === "all") return null;
        return (
            workspaces.find((workspace) => String(workspace.id) === selectedLedgerWorkspaceId) ?? null
        );
    }, [selectedLedgerWorkspaceId, workspaces]);

    const billingAccountId =
        selectedLedgerWorkspace?.billing_account_id ??
        activeWorkspace?.billing_account_id ??
        billingAccount?.id ??
        null;

    /** Only workspaces on the same billing account share this ledger. */
    const ledgerWorkspaces = React.useMemo(() => {
        if (!activeWorkspace) return workspaces;
        return workspaces.filter(
            (workspace) => workspace.billing_account_id === activeWorkspace.billing_account_id,
        );
    }, [activeWorkspace, workspaces]);

    // The 10- vs 30-day trial cohort is a backend decision; the same status
    // endpoint onboarding reads picks the link here too.
    React.useEffect(() => {
        if (isPaid || !isBillingOwner) return;
        let cancelled = false;
        void getOnboardingStatus()
            .then((status) => {
                if (!cancelled) setOfferCode(status.billing_offer_code ?? null);
            })
            .catch(() => {
                /* Falls back to the 10-day link. */
            });
        return () => {
            cancelled = true;
        };
    }, [isPaid, isBillingOwner]);

    // Loop-breaker, kept from the pre-AITS-494 panel: the landing resolver
    // (`/`) redirects here
    // whenever localStorage.landingRef carries `selected_plan` and never clears
    // the key itself, so without this a marketing visitor bounces Home → Billing
    // on every single visit until they happen to create a report.
    React.useEffect(() => {
        try {
            const raw = localStorage.getItem("landingRef");
            if (!raw) return;
            const { selected_plan } = JSON.parse(raw) ?? {};
            if (selected_plan) localStorage.removeItem("landingRef");
        } catch {
            /* Malformed landingRef — nothing to clear. */
        }
    }, []);

    React.useEffect(() => {
        setLedgerPage(1);
    }, [selectedLedgerWorkspaceId, activeWorkspace?.billing_account_id]);

    React.useEffect(() => {
        const loadTransactions = async () => {
            if (!billingAccountId) {
                setTransactions([]);
                setTransactionsCount(0);
                // Only a real problem once the workspace fetch has settled —
                // before that this is just "not loaded yet".
                setTransactionsError(
                    workspaceLoading
                        ? null
                        : "Billing account context is unavailable for the selected workspace.",
                );
                setTransactionsLoading(workspaceLoading);
                return;
            }

            try {
                setTransactionsLoading(true);
                setTransactionsError(null);
                const response = await listCreditTransactions(billingAccountId, {
                    workspaceId:
                        selectedLedgerWorkspaceId === "all"
                            ? undefined
                            : Number(selectedLedgerWorkspaceId),
                    page: ledgerPage,
                    pageSize: LEDGER_PAGE_SIZE,
                });
                setTransactions(response.results);
                setTransactionsCount(response.count);
            } catch {
                setTransactionsError("Unable to load credit history for this workspace context.");
                setTransactions([]);
                setTransactionsCount(0);
            } finally {
                setTransactionsLoading(false);
            }
        };

        void loadTransactions();
    }, [billingAccountId, ledgerPage, selectedLedgerWorkspaceId, ledgerReloadKey, workspaceLoading]);

    const totalLedgerPages = Math.max(1, Math.ceil(transactionsCount / LEDGER_PAGE_SIZE));

    const goStripe = (link?: string) => {
        if (!link) return;
        redirectToStripeWithReturnPath(link, {
            email: user?.email,
            // `/` resolves post-payment state: the shell re-checks onboarding
            // status on every route change, which is why onboarding sends
            // people there too.
            returnPath: "/",
            clientReferenceId: activeWorkspace ? `workspace:${activeWorkspace.id}` : null,
        });
    };

    const handleBuyCredits = () => {
        if (!STRIPE_ONE_TIME_CREDIT_PURCHASE_URL) return;
        redirectToStripeWithReturnPath(STRIPE_ONE_TIME_CREDIT_PURCHASE_URL, {
            email: user?.email,
            returnPath: `${location.pathname}${location.search}${location.hash}`,
            clientReferenceId: buildCreditPurchaseReference(
                activeWorkspace?.id ?? null,
                "billing_page",
            ),
        });
    };

    // Until the workspace/billing fetch settles, billingAccount is null — which
    // would otherwise read as "you are not the owner" and flash the lock + a
    // Free pill at the actual owner on every load.
    const ready = !workspaceLoading && !!billingAccount;

    const planStatusPill = !ready ? null : !isPaid ? (
        <Pill tone="neutral">Free</Pill>
    ) : isTrialing ? (
        <Pill tone="pending">Trial</Pill>
    ) : (
        <Pill tone="active-plan">Active</Pill>
    );

    return (
        <>
            {!workspaceLoading && !billingAccount && (
                <Callout tone="warn" icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}>
                    We couldn't load billing details for this workspace. Reload the page, or come
                    back shortly.
                </Callout>
            )}

            {ready && !isBillingOwner && (
                <Callout tone="neutral" icon={<Lock className="h-4 w-4" aria-hidden="true" />}>
                    Billing is managed by the workspace <strong>Owner</strong>. You can see this
                    page but can't make changes.
                </Callout>
            )}

            <section className="vb-card">
                <div className="vb-card-hd">
                    <div className="vb-card-hd__left">
                        <h2>Billing overview</h2>
                        <p className="vb-card-hd__sub">
                            Credits and capacity for {activeWorkspace?.name ?? "this workspace"}.
                        </p>
                    </div>
                    {planStatusPill}
                </div>

                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    <div className="vb-stat vb-stat--tile">
                        <span className="vb-stat__v">{billingAccount ? totalCredits : "—"}</span>
                        <span className="vb-stat__l">Credits</span>
                    </div>
                    <div className="vb-stat vb-stat--tile">
                        <span className="vb-stat__v">{billingAccount?.member_limit ?? "—"}</span>
                        <span className="vb-stat__l">Seats</span>
                    </div>
                    <div className="vb-stat vb-stat--tile">
                        <span className="vb-stat__v vb-stat__v--sm">
                            {ready ? (PLAN_LABELS[plan] ?? plan) : "—"}
                        </span>
                        <span className="vb-stat__l">Plan</span>
                    </div>
                    <div className="vb-stat vb-stat--tile">
                        <span className="vb-stat__v vb-stat__v--sm">
                            {isTrialing ? (trialEnds ?? "—") : (renewsOn ?? "—")}
                        </span>
                        <span className="vb-stat__l">{isTrialing ? "Trial ends" : "Renews"}</span>
                    </div>
                </div>
            </section>

            {ready && isBillingOwner && isPaid && (
                <>
                    {/* Whole card is gated on the env var, not just the button —
                        an unconfigured Stripe leaves an actionless card otherwise. */}
                    {STRIPE_PLAN_URL && (
                    <section className="vb-card">
                        <div className="vb-card-hd">
                            <div className="vb-card-hd__left">
                                <h2>Billing &amp; invoices</h2>
                                <p className="vb-card-hd__sub">
                                    Manage your subscription plan, download invoices, and cancel your
                                    subscription through the Stripe billing portal. Changes are
                                    effective immediately.
                                </p>
                            </div>
                            <VbButton
                                type="button"
                                onClick={() =>
                                    window.open(
                                        `${STRIPE_PLAN_URL}${
                                            STRIPE_PLAN_URL.includes("?") ? "&" : "?"
                                        }locked_prefilled_email=${encodeURIComponent(
                                            user?.email ?? "",
                                        )}`,
                                        "_blank",
                                        "noopener,noreferrer",
                                    )
                                }
                            >
                                Manage billing in Stripe
                                <ExternalLink className="h-[13px] w-[13px]" aria-hidden="true" />
                            </VbButton>
                        </div>
                    </section>
                    )}

                    {STRIPE_ONE_TIME_CREDIT_PURCHASE_URL && (
                        <section className="vb-card">
                            <div className="vb-card-hd">
                                <div className="vb-card-hd__left">
                                    <h2>Buy more credits</h2>
                                    <p className="vb-card-hd__sub">
                                        One-time credits never expire with your billing cycle. Each
                                        one runs a report.
                                    </p>
                                </div>
                                <VbButton type="button" onClick={handleBuyCredits}>
                                    Buy credits
                                </VbButton>
                            </div>
                        </section>
                    )}
                </>
            )}

            {ready && isBillingOwner && !isPaid && (
                <section className="vb-card">
                    <div className="vb-card-hd">
                        <div className="vb-card-hd__left">
                            <h2>Choose your plan</h2>
                            <p className="vb-card-hd__sub">
                                One plan, everything included. Cancel any time.
                            </p>
                        </div>
                    </div>
                    <PlanCard
                        cadence={cadence}
                        onCadenceChange={setCadence}
                        trialLabel={
                            offerCode === "v2_monthly_30"
                                ? "Start my 30-day free trial"
                                : "Start my 10-day free trial"
                        }
                        onStartTrial={() =>
                            goStripe(
                                offerCode === "v2_monthly_30"
                                    ? STRIPE_V2_MONTHLY_30
                                    : STRIPE_V2_MONTHLY_10,
                            )
                        }
                        onSubscribe={() =>
                            goStripe(
                                cadence === "annual" ? STRIPE_V2_ANNUALLY : STRIPE_V2_MONTHLY,
                            )
                        }
                    />
                    <p className="vb-fineprint">
                        Payments processed by <strong>Stripe</strong> · Cancel any time
                    </p>
                </section>
            )}

            <section className="vb-card">
                <div className="vb-card-hd">
                    <div className="vb-card-hd__left">
                        <h2>Credit history</h2>
                        <p className="vb-card-hd__sub">
                            Every credit added to or spent from this billing account.
                        </p>
                    </div>
                    {ledgerWorkspaces.length > 1 && (
                        <Select
                            value={selectedLedgerWorkspaceId}
                            onValueChange={setSelectedLedgerWorkspaceId}
                            size="sm"
                            className="vb-select__trigger--auto"
                            aria-label="Filter credit history by workspace"
                        >
                            <SelectItem value="all">All workspaces</SelectItem>
                            {ledgerWorkspaces.map((workspace) => (
                                <SelectItem key={workspace.id} value={String(workspace.id)}>
                                    {workspace.name}
                                </SelectItem>
                            ))}
                        </Select>
                    )}
                </div>

                {transactionsError ? (
                    <VbError
                        description={transactionsError}
                        onRetry={() => setLedgerReloadKey((key) => key + 1)}
                    />
                ) : transactionsLoading ? (
                    <VbLoading rows={3} />
                ) : transactions.length === 0 ? (
                    <VbEmpty
                        title="No transactions yet"
                        description="Credits added to or spent from this billing account will show up here."
                    />
                ) : (
                    // `tight` (560px) not the default 700px: the settings rail
                    // narrows the content column to ~660px on a 1280px screen,
                    // which would card-collapse every table on a laptop.
                    <Table
                        columns={LEDGER_COLUMNS}
                        minWidth={560}
                        tight
                        framed
                        ariaLabel="Credit history"
                    >
                        <TableHead>
                            <span>Date</span>
                            <span>Reason</span>
                            <span>Amount</span>
                            <span>Workspace</span>
                            <span>User</span>
                        </TableHead>
                        {transactions.map((transaction) => (
                            <TableRow key={transaction.id}>
                                <span className="vb-dt-last" data-label="Date">
                                    {formatTransactionDate(transaction.created_at)}
                                </span>
                                <span className="vb-dt-name" data-label="Reason">
                                    {transaction.reason_display ?? formatReason(transaction.reason)}
                                </span>
                                <span
                                    className={
                                        transaction.amount < 0
                                            ? "vb-amt vb-amt--debit"
                                            : "vb-amt vb-amt--credit"
                                    }
                                    data-label="Amount"
                                >
                                    {transaction.amount > 0 ? "+" : ""}
                                    {transaction.amount}
                                </span>
                                <span className="vb-dt-last" data-label="Workspace">
                                    {selectedLedgerWorkspaceId === "all"
                                        ? (transaction.workspace_name ?? "—")
                                        : (activeWorkspace?.name ??
                                          transaction.workspace_name ??
                                          "—")}
                                </span>
                                <span className="vb-dt-last" data-label="User">
                                    {transaction.user_email ?? "System"}
                                </span>
                            </TableRow>
                        ))}
                    </Table>
                )}

                <div className="vb-card-ft">
                    <p className="vb-card-ft__note">
                        Page {ledgerPage} of {totalLedgerPages} · {transactionsCount} transactions
                    </p>
                    <div className="ml-auto flex items-center gap-2">
                        <VbButton
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => setLedgerPage((prev) => Math.max(1, prev - 1))}
                            disabled={ledgerPage <= 1 || transactionsLoading || !billingAccountId}
                        >
                            Previous
                        </VbButton>
                        <VbButton
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                                setLedgerPage((prev) => Math.min(totalLedgerPages, prev + 1))
                            }
                            disabled={
                                ledgerPage >= totalLedgerPages ||
                                transactionsLoading ||
                                !billingAccountId
                            }
                        >
                            Next
                        </VbButton>
                    </div>
                </div>
            </section>
        </>
    );
};

export default SettingsBilling;
